<template>
  <div class="standard-feature-page">
    <div class="standard-feature-layout">
      <div class="standard-side-panel">
        <!-- 1. 飘屏基础配置 -->
        <div class="panel-card tech-card">
          <div class="card-header">
            <div class="icon-wrapper">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M13 8H7"/><path d="M17 12H7"/>
              </svg>
            </div>
            <span>飘屏基础设置</span>
            <label class="header-toggle" style="margin-left: auto;">
              <input type="checkbox" v-model="config.enabled" />
              <span class="toggle-track"></span>
            </label>
          </div>
          <div class="card-body">
            <div class="config-list" :class="{ disabled: !config.enabled }">
              <div class="config-item">
                <div class="label-row">
                  <label>移动速度</label>
                  <span class="value-tag">{{ config.speed }}s</span>
                </div>
                <input type="range" v-model="config.speed" min="3" max="15" step="1" class="cyber-range" />
              </div>
              <div class="config-item" style="margin-top: 16px;">
                <div class="label-row">
                  <label>弹幕密度</label>
                  <span class="value-tag">{{ config.density }}条/屏</span>
                </div>
                <input type="range" v-model="config.density" min="5" max="50" step="5" class="cyber-range" />
              </div>
              <div class="config-item" style="margin-top: 16px;">
                <label>展示区域</label>
                <div class="template-grid">
                  <button class="tpl-btn" :class="{ active: config.area === 'full' }" @click="config.area = 'full'">全屏</button>
                  <button class="tpl-btn" :class="{ active: config.area === 'top' }" @click="config.area = 'top'">顶部</button>
                  <button class="tpl-btn" :class="{ active: config.area === 'bottom' }" @click="config.area = 'bottom'">底部</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="standard-center-preview">
        <div class="standard-phone-frame">
          <div class="standard-phone-screen">
            <!-- 实时推流预览层 (当驱动开启时显示) -->
            <img 
              v-if="isVirtualCameraLive"
              key="fixed-live-stream-live-float"
              class="video-element live-stream-preview"
              src="http://127.0.0.1:3000/stream"
              :style="{ position: 'absolute', inset: 0, zIndex: 10, width: '100%', height: '100%', objectFit: 'cover' }"
            />

            <!-- 真实视频播放器 - 推流模式下不加载真实源以避免解码冲突 -->
            <video v-if="videoUrl && !isVirtualCameraLive" 
                   ref="videoPlayerRef"
                   :src="videoUrl" 
                   :muted="isMuted"
                   class="standard-video-player" 
                   playsinline
                   crossorigin="anonymous"
                   @play="onPlayerPlay"
                   @pause="onPlayerPause"
                   @timeupdate="onPlayerTimeUpdate"
                   @loadedmetadata="onPlayerLoadedMetadata"
                   @ended="onVideoEnded"
                   @error="onPlayerError"></video>

            <!-- 直播间 UI 遮罩 + 飘屏层 -->
            <div class="live-ui-overlay">
              <div class="live-header">
                <div class="user-info-capsule">
                  <div class="avatar"><img src="https://api.dicebear.com/7.x/avataaars/svg?seed=Douyin" alt="avatar"></div>
                  <div class="meta">
                    <div class="name">MULTIFACED</div>
                    <div class="stats">12.8w 点赞</div>
                  </div>
                  <div class="follow">+ 关注</div>
                </div>
                <div class="viewer-tag">
                  <span class="val">3821</span>
                  <span class="lab">人</span>
                </div>
              </div>

              <!-- 模拟飘屏弹幕 -->
              <div v-if="config.enabled" class="danmaku-container" :class="'area-' + config.area">
                <div v-for="n in 8" :key="n" 
                     class="danmaku-item"
                     :style="{ 
                       top: getRandomTop(n), 
                       animationDuration: (config.speed - 1 + Math.random() * 2) + 's',
                       animationDelay: (n * 0.8) + 's',
                       opacity: 0.8 + Math.random() * 0.2
                     }">
                  {{ config.messages[Math.floor(Math.random() * config.messages.length)] }}
                </div>
              </div>

              <div class="live-bottom-area">
                <div class="comment-gradient"></div>
              </div>
            </div>
          </div>

          <!-- 播放器控制条 -->
          <div class="player-control-bar" v-if="videoUrl">
            <div class="play-btn-mini" @click="togglePlayback">
              <svg v-if="!isPlaying" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
              <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
            </div>
            <div class="progress-section">
              <div class="progress-bar-wrap" @mousedown="startSeek">
                <div class="progress-bg"><div class="progress-fill" :style="{ width: progressPercent + '%' }"></div></div>
              </div>
              <div class="time-info"><span>{{ formatTime(currentTime) }}</span><span>/</span><span>{{ formatTime(duration) }}</span></div>
            </div>
            <div class="volume-section">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V5L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" /></svg>
              <input type="range" min="0" max="1" step="0.1" v-model="volume" class="volume-slider-mini" />
            </div>
          </div>
        </div>
      </div>

      <div class="standard-side-panel">
        <!-- 2. 弹幕内容管理 -->
        <div class="panel-card tech-card">
          <div class="card-header">
            <div class="icon-wrapper">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </div>
            <span>弹幕库管理</span>
            <button class="icon-reset-btn" title="清空" @click="config.messages = []" style="margin-left: auto;">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </button>
          </div>
          <div class="card-body" style="min-height: 480px;">
            <div class="config-item">
              <div class="input-group-mini">
                <input type="text" v-model="newMessage" class="cyber-input-mini" placeholder="添加新弹幕内容..." @keyup.enter="addMessage" />
                <button class="send-btn-mini" @click="addMessage">添加</button>
              </div>
            </div>
            <div class="selected-list" style="margin-top: 12px;">
              <div v-for="(msg, idx) in config.messages" :key="idx" class="selected-item">
                <div class="s-info"><div class="s-name" style="font-size: 11px;">{{ msg }}</div></div>
                <button class="del-btn-icon" @click="config.messages.splice(idx, 1)">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"></path></svg>
                </button>
              </div>
              <div v-if="config.messages.length === 0" class="empty-tip" style="margin-top: 40px;">弹幕库为空</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "live-float",
  data() {
    return {
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      progressPercent: 0,
      volume: 1,
      isMuted: false,
      newMessage: '',
      danmakuList: [],
      danmakuTimer: null
    };
  },
  computed: {
    config() {
      return this.$store.state.liveFloatParams;
    },
    videoUrl() {
      const media = this.$store.state.currentMedia;
      if (media && media.type === 'video') return media.url;
      return '';
    },
    isVirtualCameraLive() {
      return this.$store.state.isVirtualCameraLive;
    }
  },
  mounted() {
    if (this.videoUrl) {
      this.$nextTick(() => {
        this.initPlayer();
      });
    }
  },
  beforeDestroy() {
    /*this.stopDanmaku();*/
  },
  methods: {
    initPlayer() {
      if (this.$refs.videoPlayerRef) {
        this.$refs.videoPlayerRef.addEventListener('loadedmetadata', () => {
          this.duration = this.$refs.videoPlayerRef.duration;
        });
      }
    },
    onPlayerPlay() {
      this.isPlaying = true;
      this.startDanmaku();
    },
    onPlayerPause() {
      this.isPlaying = false;
      this.stopDanmaku();
    },
    onPlayerTimeUpdate() {
      if (this.$refs.videoPlayerRef) {
        this.currentTime = this.$refs.videoPlayerRef.currentTime;
        this.progressPercent = (this.currentTime / this.duration) * 100;
      }
    },
    onPlayerLoadedMetadata() {
      if (this.$refs.videoPlayerRef) {
        this.duration = this.$refs.videoPlayerRef.duration;
      }
    },
    onVideoEnded() {
      this.isPlaying = false;
      this.stopDanmaku();
    },
    onPlayerError() {
      console.error('Video player error');
    },
    onStreamError() {
      console.error('Stream error');
    },
    togglePlayback() {
      if (this.$refs.videoPlayerRef) {
        if (this.isPlaying) {
          this.$refs.videoPlayerRef.pause();
        } else {
          this.$refs.videoPlayerRef.play();
        }
      }
    },
    startSeek(e) {
      e.preventDefault();
      if (this.$refs.videoPlayerRef) {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percent = Math.max(0, Math.min(1, x / rect.width));
        this.$refs.videoPlayerRef.currentTime = percent * this.duration;
      }
    },
    formatTime(seconds) {
      if (!isFinite(seconds)) return '00:00';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    },
    addMessage() {
      if (this.newMessage.trim()) {
        this.config.messages.push(this.newMessage.trim());
        this.newMessage = '';
      }
    },
    startDanmaku() {
      if (this.config.enabled && this.config.messages.length > 0) {
        this.danmakuTimer = setInterval(() => {
          this.addDanmaku();
        }, 2000 / this.config.speed);
      }
    },
    stopDanmaku() {
      if (this.danmakuTimer) {
        clearInterval(this.danmakuTimer);
        this.danmakuTimer = null;
      }
    },
    addDanmaku() {
      if (!this.config.enabled || !this.config.messages.length) return;
      
      const msg = this.config.messages[Math.floor(Math.random() * this.config.messages.length)];
      const id = Date.now();
      const areas = [];
      if (this.config.areaTop) areas.push('top');
      if (this.config.areaBottom) areas.push('bottom');
      
      const area = areas.length > 0 ? areas[Math.floor(Math.random() * areas.length)] : 'top';
      
      this.danmakuList.push({
        id,
        text: msg,
        area: area,
        top: area === 'top' ? Math.random() * 30 : 60 + Math.random() * 30,
        duration: 6 / this.config.speed,
        fontSize: this.config.fontSize
      });
      
      setTimeout(() => {
        this.danmakuList = this.danmakuList.filter(d => d.id !== id);
      }, 10000);
    },
    handleResetClick(e, resetFn) {
      e.stopPropagation();
      if (resetFn && typeof resetFn === 'function') {
        resetFn();
      }
    },
    resetLiveFloatConfig() {
      this.config.enabled = false;
      this.config.speed = 1;
      this.config.fontSize = 14;
      this.config.areaTop = true;
      this.config.areaBottom = true;
      this.config.messages = ['666', '好看', '主播加油！', '关注了'];
    },
    startDrag(e, type) {
      console.log('Drag start:', type, e);
    }
  },
  watch: {
    videoUrl() {
      if (this.videoUrl) {
        this.$nextTick(() => {
          this.initPlayer();
        });
      }
    },
    'config.enabled'(val) {
      if (val) {
        if (this.isPlaying) {
          this.startDanmaku();
        }
      } else {
        this.stopDanmaku();
      }
    }
  }
};
</script>

<style scoped>
.panel-card {
  background: rgba(20, 20, 20, 0.6); backdrop-filter: blur(10px); border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px; overflow: hidden; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 212, 255, 0.08);
}
.tech-card { position: relative; }
.tech-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.4), transparent);
}
.card-header {
  display: flex; align-items: center; gap: 10px; padding: 12px 14px;
  background: linear-gradient(180deg, #151515 0%, #0f0f0f 100%); border-bottom: 1px solid #2a2a2a;
  color: #b0b0b0; font-size: 12px; font-weight: 600;
}
.card-header span { font-size: 13px; font-weight: 600; color: #fff; flex: 1; }
.icon-wrapper {
  width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;
  background: rgba(0, 212, 255, 0.12); border-radius: 8px; color: #00d4ff;
}

.card-body { padding: 10px; min-height: 145px; display: flex; flex-direction: column; }
.config-list.disabled { opacity: 0.5; pointer-events: none; }

/* 飘屏列表样式 */
.selected-list { display: flex; flex-direction: column; gap: 8px; max-height: 400px; overflow-y: auto; }
.selected-item {
  display: flex; align-items: center; gap: 12px; padding: 10px;
  background: rgba(255, 255, 255, 0.03); border: 1px solid #2a2a2a; border-radius: 8px;
}
.s-info { flex: 1; }
.s-name { font-size: 12px; color: #fff; font-weight: 600; }
.del-btn-icon { background: none; border: none; color: #555; cursor: pointer; padding: 4px; transition: color 0.2s; }
.del-btn-icon:hover { color: #ff6b6b; }
.empty-tip { text-align: center; color: #444; font-size: 12px; }

/* 配置项通用样式 */
.config-item { margin-bottom: 12px; }
.label-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.label-row label { font-size: 11px; color: #888; }
.value-tag { font-size: 10px; color: #00d4ff; background: rgba(0, 212, 255, 0.1); padding: 1px 6px; border-radius: 4px; }
.cyber-range {
  width: 100%; height: 4px; -webkit-appearance: none; background: #2a2a2a; border-radius: 2px; outline: none;
}
.cyber-range::-webkit-slider-thumb {
  -webkit-appearance: none; width: 12px; height: 12px; background: #00d4ff; border-radius: 50%; cursor: pointer; box-shadow: 0 0 10px #00d4ff;
}

/* 模板按钮 */
.template-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 8px; }
.tpl-btn {
  background: rgba(255, 255, 255, 0.05); border: 1px solid #333; color: #888;
  padding: 6px; border-radius: 6px; font-size: 11px; cursor: pointer; transition: all 0.2s;
}
.tpl-btn:hover { background: rgba(255, 255, 255, 0.08); border-color: #444; }
.tpl-btn.active { background: rgba(0, 212, 255, 0.1); border-color: #00d4ff; color: #00d4ff; }

/* 输入组 */
.input-group-mini { display: flex; gap: 8px; margin-bottom: 12px; }
.cyber-input-mini {
  flex: 1; background: rgba(0,0,0,0.3); border: 1px solid #333; border-radius: 6px;
  padding: 6px 10px; color: #fff; font-size: 12px; outline: none;
}
.cyber-input-mini:focus { border-color: #00d4ff; box-shadow: 0 0 10px rgba(0, 212, 255, 0.2); }
.send-btn-mini {
  background: #00d4ff; color: #000; border: none; border-radius: 6px;
  padding: 0 12px; font-size: 12px; font-weight: 600; cursor: pointer;
}

/* 开关样式 */
.header-toggle { position: relative; width: 32px; height: 18px; cursor: pointer; }
.header-toggle input { opacity: 0; width: 0; height: 0; }
.toggle-track {
  position: absolute; inset: 0; background: #333; border-radius: 18px; transition: .3s;
}
.toggle-track::before {
  content: ""; position: absolute; height: 14px; width: 14px; left: 2px; bottom: 2px; background: #fff; border-radius: 50%; transition: .3s;
}
input:checked + .toggle-track { background: #00d4ff; }
input:checked + .toggle-track::before { transform: translateX(14px); }

/* 弹幕动画 */

.danmaku-container { position: absolute; inset: 0; overflow: hidden; }
.danmaku-container.area-top { height: 40%; }
.danmaku-container.area-bottom { top: 60%; height: 40%; }

.danmaku-item {
  position: absolute; white-space: nowrap; color: #fff; font-size: 14px;
  text-shadow: 0 2px 4px rgba(0,0,0,0.5); animation: danmakuMove linear infinite; left: 100%;
}
@keyframes danmakuMove { from { left: 100%; } to { left: -120%; } }

.live-bottom-area { margin-top: auto; padding: 12px; position: relative; }
.comment-gradient { position: absolute; bottom: 0; left: 0; width: 100%; height: 150px; background: linear-gradient(to top, rgba(0,0,0,0.8), transparent); z-index: -1; }
.comments-mock { display: flex; flex-direction: column; gap: 6px; }
.comment { background: rgba(0, 0, 0, 0.3); padding: 4px 10px; border-radius: 10px; font-size: 12px; color: #fff; width: fit-content; }
.comment span { color: #ffeb3b; font-weight: 700; margin-right: 4px; }

.player-control-bar {
  position: absolute; bottom: 0; left: 0; width: 100%; height: 0; opacity: 0; overflow: hidden;
  background: rgba(26, 26, 26, 0.9); backdrop-filter: blur(10px); padding: 0 16px; display: flex; align-items: center; gap: 15px;
  border-top: 1px solid rgba(255,255,255,0.05); transition: all 0.3s; z-index: 60; pointer-events: auto;
}
.standard-phone-frame:hover .player-control-bar { height: 60px; opacity: 1; padding: 10px 15px; }
.play-btn-mini { color: #00d4ff; cursor: pointer; display: flex; align-items: center; }
.progress-section { flex: 1; display: flex; flex-direction: column; gap: 6px; }
.progress-bar-wrap { height: 4px; background: rgba(255,255,255,0.1); border-radius: 2px; cursor: pointer; position: relative; }
.progress-fill { position: absolute; inset: 0 auto 0 0; background: #00d4ff; border-radius: 2px; box-shadow: 0 0 10px rgba(0,212,255,0.5); }
.time-info { display: flex; gap: 4px; font-size: 10px; color: #888; font-family: monospace; }
.volume-section { display: flex; align-items: center; gap: 8px; color: #888; }
.volume-slider-mini { width: 60px; height: 3px; -webkit-appearance: none; background: rgba(255,255,255,0.1); border-radius: 2px; outline: none; }
.volume-slider-mini::-webkit-slider-thumb { -webkit-appearance: none; width: 8px; height: 8px; background: #fff; border-radius: 50%; cursor: pointer; }

.icon-reset-btn { background: none; border: none; color: #666; cursor: pointer; padding: 4px; border-radius: 4px; transition: all 0.3s; }
.icon-reset-btn:hover { color: #ff6b6b; background: rgba(255, 107, 107, 0.1); }
</style>
